<!--
 * @Author: Nemo
 * @Description: OnSelect组件 - 统一风格的基础下拉选择框组件
 * @Date: 2025-04-18
 * @LastEditors: Nemo
 * @FilePath: /rhcn-checkout-web/src/componentsV2/base/select/index.vue
-->
<template>
  <div class="on-select-container" :class="{ 'is-error': error, 'is-disabled': disabled }">
    <div class="on-select" :class="{ 'focus': isFocus, 'disabled': disabled, 'error': error }">
      <select
        class="on-select__native"
        :style="{ '--focus-border-color': focusBorderColor }"
        :value="value"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        :disabled="disabled"
        :required="required"
      >
        <option v-if="placeholder" value="" disabled selected>{{ placeholder }}</option>
        <option
          v-for="(option, index) in options"
          :key="index"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>
      <div class="on-select__arrow"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'OnSelect',
  props: {
    /**
     * 下拉框当前选中的值
     */
    value: {
      type: [String, Number],
      default: '',
    },
    /**
     * 可选项数据数组
     * 格式: [{ value: 'value1', label: 'label1', disabled: false }]
     * 每个选项必须包含 value 和 label 属性，disabled 为可选
     */
    options: {
      type: Array,
      default: () => [],
      validator: (options) => {
        return options.every(option =>
          Object.prototype.hasOwnProperty.call(option, 'value') &&
          Object.prototype.hasOwnProperty.call(option, 'label'),
        );
      },
    },
    /**
     * 下拉框占位文本，当没有选中值时显示
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * 是否禁用整个下拉框
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否为必填项
     */
    required: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否显示错误状态样式
     */
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * 当前下拉框是否处于聚焦状态
       */
      isFocus: false,
    };
  },
  computed: {
    ...mapGetters(['app']),
    /**
     * 获取主题色
     * 优先使用app.brandInfo.color，如果不存在则使用$color-text-primary
     * @returns {String} 主题色值
     */
    themeColor() {
      return this.app && this.app.brandInfo && this.app.brandInfo.color
        ? this.app.brandInfo.color
        : '';
    },
    /**
     * 获取聚焦时的边框颜色
     * 优先使用themeColor，否则返回空（将使用CSS中的$color-purple-primary）
     * @returns {String} 边框颜色值
     */
    focusBorderColor() {
      return this.themeColor || '';
    },
  },
  methods: {
    /**
     * 处理下拉框聚焦事件
     * 设置isFocus为true并向上传递focus事件
     * @param {Event} event - 聚焦事件对象
     */
    handleFocus(event) {
      this.isFocus = true;
      this.$emit('focus', event);
    },
    /**
     * 处理下拉框失焦事件
     * 设置isFocus为false并向上传递blur事件
     * @param {Event} event - 失焦事件对象
     */
    handleBlur(event) {
      this.isFocus = false;
      this.$emit('blur', event);
    },
    /**
     * 处理选择值变化事件
     * 向上传递input事件和change事件
     * @param {Event} event - 变化事件对象
     */
    handleChange(event) {
      this.$emit('input', event.target.value);
      this.$emit('change', event);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.on-select-container {
  width: 100%;
  position: relative;

  .on-select {
    position: relative;
    width: 100%;
    font-size: 14px;

    &__native {
      width: 100%;
      height: 38px;
      background: #FFFFFF;
      box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
      border-radius: 8px;
      border: 1px solid #D0D5DD;
      padding: 0 30px 0 12px;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      font-size: 16px;
      color: $color-text;
      transition: all 0.3s;
      outline: none;
      box-sizing: border-box;

      &:focus {
        outline: none;
      }

      &::placeholder {
        color: $color-placeholder;
        font-size: 16px;
      }
    }

    // 自定义下拉箭头样式
    &__arrow {
      position: absolute;
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
      pointer-events: none;
      width: 16px;
      height: 16px;
      font-size: 10px;
      color: #606266;
      transition: transform 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      // 图标本身的尺寸
      &::before {
        content: '';
        width: 9.33px;
        height: 5.33px;
        background-image: url("data:image/svg+xml,%3csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M1 1L5 5L9 1' stroke='%23D0D5DD' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
      }
    }

    // 聚焦状态样式
    &.focus {
      .on-select__native {
        border-color: var(--focus-border-color, $color-purple-primary);
        box-shadow: 0px 0px 0px 3px #F1F1FF;
      }

      .on-select__arrow {
        transform: translateY(-50%) rotate(180deg);

        &::before {
          transform: rotate(180deg);
        }
      }
    }

    // 禁用状态样式
    &.disabled {
      .on-select__native {
        background-color: #F0F3F6;
        color: #9EA9C1;
        cursor: not-allowed;
      }
    }

    // 错误状态样式
    &.error {
      .on-select__native {
        border-color: $color-form-error;
        box-shadow: none;
      }
    }
  }

  // 错误信息过渡动画（下方）
  .slide-fade-enter-active {
    transition: all 0.3s ease;
  }
  .slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  }
  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
  }
}
</style>
